import React from "react";
import { Link } from "react-router-dom";
import { mediaUrl } from "@/lib/api";
import { imageSrcSet, SIZES } from "@/lib/responsiveImage";
import { statusBadge, PRICE_QUALIFIER_SHORT } from "@/lib/propertyStatus";
import { fmtPrice } from "@/lib/terms";
import { propertyPath } from "@/lib/propertyUrl";

/**
 * One listing, as the design draws it.
 *
 * The second photograph is preloaded behind the first and crossfades on
 * hover: that is the design's own trick and it is the reason the card feels
 * like an estate agent's rather than a table row. A listing with one
 * photograph simply does not do it.
 *
 * Everything on it is the record's: the street (the house number is taken
 * off server-side, see backend/addressing.py), the town, the outcode, the
 * type, the qualifier, the price and the three specs. There is no copy here
 * to belong to one agency.
 */
export default function EstatePropertyCard({ property: p, market = "uk", href, feature = false }) {
  const images = p.images || [];
  const primary = images.find((i) => i.id === p.primary_image_id) || images[0];
  const second = images.find((i) => i !== primary);
  const badge = statusBadge(p.status, p.listing_type, market);
  const letting = p.listing_type === "letting";
  const amount = letting ? p.rental_pcm : p.asking_price;
  const qualifier = letting ? "" : (PRICE_QUALIFIER_SHORT[p.price_qualifier] || "");

  // The outcode only, matching the URL and for the same reason: an incode
  // narrows a postcode to a handful of houses.
  const outcode = String(p.postcode || "").trim().split(/\s+/)[0];
  const place = [p.town, outcode, p.property_type].filter(Boolean).join(" · ");
  const alt = [p.address_line1, p.town].filter(Boolean).join(", ");

  const specs = [
    p.bedrooms ? { v: p.bedrooms, k: "bed" } : null,
    p.bathrooms ? { v: p.bathrooms, k: "bath" } : null,
    p.size_sqft ? { v: p.size_sqft.toLocaleString(), k: "sq ft" } : null,
  ].filter(Boolean);

  return (
    <Link className={`pcard${feature ? " feature" : ""}`} to={href || propertyPath(p)}>
      <div className="pcard-media">
        {primary ? (
          <img src={mediaUrl(primary.url)} alt={alt} loading={feature ? "eager" : "lazy"}
               srcSet={imageSrcSet(primary)} sizes={feature ? SIZES.cardFeature : SIZES.card}
               decoding="async" width="800" height="600" />
        ) : (
          <div style={{ width: "100%", height: "100%" }} aria-hidden="true" />
        )}
        {second && (
          <img className="alt" src={mediaUrl(second.url)} alt="" loading="lazy"
               srcSet={imageSrcSet(second)} sizes={feature ? SIZES.cardFeature : SIZES.card}
               decoding="async" aria-hidden="true" />
        )}
        {badge && <span className={`tag${badge.tone === "muted" ? " dark" : ""}`}>{badge.label}</span>}
      </div>
      <div className="pcard-body">
        <div className="addr">{p.address_line1}</div>
        {place && <div className="loc">{place}</div>}
        {amount ? (
          <div className="price num">
            {qualifier && <small>{qualifier}</small>}
            {fmtPrice(amount, market)}{letting ? " pcm" : ""}
          </div>
        ) : null}
        {specs.length > 0 && (
          <div className="specs num">
            {specs.map((s) => <span key={s.k}><b>{s.v}</b> {s.k}</span>)}
          </div>
        )}
      </div>
    </Link>
  );
}
